import { defineComponent as _defineComponent } from 'vue'
import { PropType } from 'vue'
import { ref, computed, onMounted, watch, onBeforeUnmount, nextTick, Ref, useRoute } from '@nuxtjs/composition-api'
import { storeToRefs } from 'pinia'
import IconLotPlaceholder from '~/components/Icon/IconLotPlaceholder.vue'
import IconClose from '~/components/Icon/IconClose.vue'
// Amplitude
import { amplitudeLogEvent, amplitudeEvents } from '@shared/lib'
import { togglePageScroll } from '@shared/utils'
import { IProduct, ILabel, ITransformLabels, IModifier } from './../model/types'
import { useRestaurantModel } from '@entities/restaurant'
import { useThemeModel } from '@entities/theme'
import { useMenuModel } from './../model'
import { isCartDisabledForTable } from '@entities/restaurant/utils/cartRules'

interface NutritionInfo {
  calories: number | null
  protein: number | null
  fat: number | null
  carbs: number | null
}

interface ProductNutritionInfo {
  per_100g: NutritionInfo
  show_per_100g: boolean
  show_total: boolean
  total: NutritionInfo
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MenuDetailModal',
  props: {
  product: {
    type: Object as PropType<IProduct & {
      nutrition_info?: ProductNutritionInfo
    }>,
    required: true
  },
  visible: {
    type: Boolean,
    default: false
  },
  fromCart: {
    type: Boolean,
    default: false
  },
  cartItem: {
    type: Object as PropType<any>,
    required: false,
    default: null
  }
},
  emits: ['close', 'after-leave'],
  setup(__props, { emit }) {

const props = __props





const themeModel = useThemeModel()
const restaurantModel = useRestaurantModel()
const menuModel = useMenuModel()
const route = useRoute()
const autoImageHeightRestaurantCodes = new Set<string>([
  'gruzinskie-istorii-26701',
  'gruzinskie-kanikuli-26669',
  'gamardghoba-22398',
  'gamardghoba-22391',
  'gruzinskie-kanikuli-rostov-21823',
  'gruzinskie-kanikuli-21231',
  'gruzinskie-kanikuli-altufevo-21202',
  'hrust-21963',
  'hrust-21311',
  'gamardghoba-21239',
  'gamardghoba-21201',
  'gruzinskie-kanikuli-21208',
  'kafe-gruzinskoi-kuhni-gamardghoba-21203',
  'gamardghoba-21206',
  'gruzinskie-kanikuli-19936'
  // 'iiko2'
])
const {
  stopListModifierIds,
  stopListExternalModifierIds,
  stopListGlobalExternalModifierIds,
  stopListExternalProductIds
} = storeToRefs(menuModel)

const tableId = computed(() => restaurantModel.tableCode || route.value.params.id)
const isAddControlsVisible = computed(() => {
  const isDisabled = isCartDisabledForTable(restaurantModel.code, tableId.value)
  return restaurantModel.isCartEnabled && !isDisabled
})

const imgSrc = ref<string>('')
const isImageLoading = ref<boolean>(false)
const isMounted = ref<boolean>(false)
const isVisible = ref<boolean>(false)
const isImagePreviewOpen = ref<boolean>(false)
const lastLoadedImageKey = ref<string>('')
const ANIMATION_DURATION = 300
let leaveTimer: ReturnType<typeof setTimeout> | null = null
let addPriceFrame: number | null = null
let removePriceFrame: number | null = null

const previewImageSrc = computed(() => {
  if (imgSrc.value) {
    return imgSrc.value
  }
  const picture = props.product.product_picture
  return picture?.versions?.lazyLoad ?? picture?.versions?.optimized ?? picture?.versions?.original ?? ''
})

// Объявляем refs для размеров и модификаторов до использования в showModal
const selectedSizeId = ref<string | null>(null)
const selectedModifiers = ref<number[]>([])
const modifierQuantities = ref<Record<number, number>>({})
const selectedQuantity = ref<number>(1)
const groupSelections = ref<Record<number, number[] | number | null>>({})
const contentWrapperRef = ref<HTMLElement | null>(null)
let touchStartY = 0

const handleContentTouchStart = (event: TouchEvent) => {
  touchStartY = event.touches[0].clientY
}

const handleContentTouchMove = (event: TouchEvent) => {
  const el = contentWrapperRef.value
  if (!el) {
    return
  }

  const canScroll = el.scrollHeight > el.clientHeight
  if (!canScroll) {
    event.preventDefault()
    return
  }

  const currentY = event.touches[0].clientY
  const deltaY = currentY - touchStartY
  const atTop = el.scrollTop <= 0
  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1

  if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
    event.preventDefault()
  }
}

const lockPageScroll = () => {
  if (typeof window === 'undefined') {
    return
  }
  togglePageScroll(true)
}

const unlockPageScroll = () => {
  if (typeof window === 'undefined') {
    return
  }
  togglePageScroll(false)
}

const clearLeaveTimer = () => {
  if (leaveTimer) {
    clearTimeout(leaveTimer)
    leaveTimer = null
  }
}

const getImageKey = () => {
  const picture = props.product.product_picture
  return picture?.versions?.webp ??
    picture?.versions?.lazyLoad ??
    picture?.versions?.optimized ??
    picture?.versions?.original ??
    ''
}

const loadProductImage = () => {
  const picture = props.product.product_picture
  const hasImage = Boolean(picture)
  const nextImageKey = getImageKey()

  // Если открываем ту же картинку повторно — не трогаем состояние загрузки,
  // чтобы не дергать скелетон и не вызывать мигание.
  if (nextImageKey && nextImageKey === lastLoadedImageKey.value) {
    isImageLoading.value = false
    isImagePreviewOpen.value = false
    return
  }

  lastLoadedImageKey.value = nextImageKey
  imgSrc.value = ''
  isImageLoading.value = hasImage
  isImagePreviewOpen.value = false

  if (picture?.versions?.webp) {
    imgSrc.value = picture.versions.webp
  } else if (!picture?.versions?.original && !hasImage) {
    isImageLoading.value = false
  }
}

const handleImageLoaded = () => {
  isImageLoading.value = false
}

const handleImageError = () => {
  isImageLoading.value = false
}

const openImagePreview = () => {
  if (!previewImageSrc.value) {
    return
  }
  isImagePreviewOpen.value = true
}

const closeImagePreview = () => {
  isImagePreviewOpen.value = false
}

const isModifierInStopList = (modifierId: number, modifierExternalId?: string, modifierExternalProductId?: string): boolean => {
  const productId = props.product.id
  const externalProductId = props.product.external_id

  // Проверка по product_id
  if (productId && stopListModifierIds.value.has(productId)) {
    if (stopListModifierIds.value.get(productId)!.has(modifierId)) {
      return true
    }
  }

  // Проверка по external_product_id
  if (externalProductId && modifierExternalId && stopListExternalModifierIds.value.has(String(externalProductId))) {
    if (stopListExternalModifierIds.value.get(String(externalProductId))!.has(String(modifierExternalId))) {
      return true
    }
  }

  // Глобальная проверка по external_modifier_id (для случаев, когда external_product_id = null)
  if (modifierExternalId && stopListGlobalExternalModifierIds.value.has(String(modifierExternalId))) {
    return true
  }

  // Проверка по external_product_id модификатора (для обычных модификаторов)
  if (modifierExternalProductId && stopListGlobalExternalModifierIds.value.has(String(modifierExternalProductId))) {
    return true
  }

  // Проверка, если external_id модификатора совпадает с external_product_id продукта в стоп-листе
  // (для случаев, когда обычный модификатор имеет external_id, совпадающий с external_product_id продукта в стоп-листе)
  if (modifierExternalId && stopListExternalProductIds.value.has(String(modifierExternalId))) {
    return true
  }

  return false
}

const showModal = () => {
  clearLeaveTimer()
  if (!isMounted.value) {
    isMounted.value = true
  }
  lockPageScroll()

  // Проверяем, что продукт загружен
  if (!props.product || !props.product.id) {
    return
  }

  // Используем nextTick, чтобы убедиться, что все computed свойства инициализированы
  nextTick(() => {
    // Проверяем, нужно ли открывать в дефолтном состоянии (если блюдо в стоп-листе или имеет недоступные модификаторы)
    const shouldOpenAsNew = props.cartItem?._openAsNew || false

    if (props.fromCart && props.cartItem && !shouldOpenAsNew) {
      // При открытии из корзины - загружаем модификаторы из элемента корзины
      const cartItemModifiers = props.cartItem.selectedModifiers || []
      const cartItemSize = props.cartItem.selectedSize || null

      // Разделяем модификаторы на те, что с количеством и без
      const quantities: Record<number, number> = {}
      const simpleModifiers: number[] = []

      if (allModifiersMap.value) {
        cartItemModifiers.forEach((modifierId: number) => {
          const modifier = allModifiersMap.value.get(modifierId)
          if (modifier && modifier.max_count > 1 && !isModifierInGroup(modifierId)) {
            quantities[modifierId] = (quantities[modifierId] || 0) + 1
          } else {
            simpleModifiers.push(modifierId)
          }
        })
      }

      // Устанавливаем модификаторы и размер из элемента корзины
      modifierQuantities.value = quantities
      selectedModifiers.value = simpleModifiers
      menuModel.setSelectedModifiers(props.product.id, [...cartItemModifiers])

      if (cartItemSize) {
        menuModel.setSelectedSize(props.product.id, cartItemSize)
        selectedSizeId.value = cartItemSize
      } else {
        menuModel.clearSelectedSize(props.product.id)
        selectedSizeId.value = null
      }

      // Устанавливаем количество из корзины
      selectedQuantity.value = props.cartItem.inCart || 1

      syncGroupSelections()
    } else if (props.fromCart && props.cartItem && shouldOpenAsNew) {
      // Открываем как новый вариант: сохраняем выбранные ранее модификаторы, но фильтруем те, что в стоп-листе
      const cartItemModifiers = props.cartItem.selectedModifiers || []
      const cartItemSize = props.cartItem.selectedSize || null
      const cartItemId = props.cartItem.cartItemId || String(props.cartItem.id)
      const unavailableByCart = menuModel.unavailableModifierIds.get(cartItemId) || new Set<number>()

      const quantities: Record<number, number> = {}
      const simpleModifiers: number[] = []

      if (allModifiersMap.value) {
        cartItemModifiers.forEach((modifierId: number) => {
          const modifier = allModifiersMap.value.get(modifierId)
          // Пропускаем модификаторы, которые в стоп-листе
          if (
            unavailableByCart.has(modifierId) ||
            (modifier && isModifierInStopList(modifierId, modifier.external_id, modifier.external_product_id))
          ) {
            return
          }

          if (modifier && modifier.max_count > 1 && !isModifierInGroup(modifierId)) {
            quantities[modifierId] = (quantities[modifierId] || 0) + 1
          } else {
            simpleModifiers.push(modifierId)
          }
        })
      }

      modifierQuantities.value = quantities
      selectedModifiers.value = simpleModifiers
      menuModel.setSelectedModifiers(props.product.id, [...simpleModifiers, ...Object.keys(quantities).map(id => Number(id))])

      if (cartItemSize) {
        menuModel.setSelectedSize(props.product.id, cartItemSize)
        selectedSizeId.value = cartItemSize
      } else {
        menuModel.clearSelectedSize(props.product.id)
        selectedSizeId.value = null
      }

      // Устанавливаем количество из корзины
      selectedQuantity.value = props.cartItem.inCart || 1

      syncGroupSelections()
    } else {
      // При открытии из меню - очищаем модификаторы (кроме предвыбранных)
      // Очищаем сохраненные модификаторы
      menuModel.clearSelectedModifiers(props.product.id)
      menuModel.clearSelectedSize(props.product.id)
      selectedModifiers.value = []
      modifierQuantities.value = {}
      selectedSizeId.value = null
      syncGroupSelections()
      // Инициализируем только предвыбранные модификаторы (с min_count > 0)
      initializeModifiersWithMinCount()
    }
  })

  requestAnimationFrame(() => {
    isVisible.value = true
  })
}

const hideModal = () => {
  isVisible.value = false
  clearLeaveTimer()
  unlockPageScroll()
  // Если продукт не добавлен в корзину, очищаем выбранные модификаторы и размер
  if (productInCart.value === 0 || !productInCart.value) {
    menuModel.clearSelectedModifiers(props.product.id)
    menuModel.clearSelectedSize(props.product.id)
    selectedModifiers.value = []
    modifierQuantities.value = {}
    selectedSizeId.value = null
    syncGroupSelections()
  }
  leaveTimer = setTimeout(() => {
    isMounted.value = false
    emit('after-leave')
  }, ANIMATION_DURATION)
}

const animatePriceValue = (refValue: Ref<number>, target: number, frameRef: 'add' | 'remove') => {
  if (typeof window === 'undefined') {
    refValue.value = target
    return
  }
  const cancelFrame = () => {
    if (frameRef === 'add' && addPriceFrame) {
      cancelAnimationFrame(addPriceFrame)
      addPriceFrame = null
    }
    if (frameRef === 'remove' && removePriceFrame) {
      cancelAnimationFrame(removePriceFrame)
      removePriceFrame = null
    }
  }
  cancelFrame()
  const startValue = refValue.value
  const change = target - startValue
  if (change === 0) {
    refValue.value = target
    return
  }
  const duration = 500
  const start = performance.now()
  const easeOut = (t: number) => 1 - Math.pow(1 - t, 3)
  const step = (time: number) => {
    const progress = Math.min((time - start) / duration, 1)
    refValue.value = Math.round(startValue + change * easeOut(progress))
    if (progress < 1) {
      const frameId = requestAnimationFrame(step)
      if (frameRef === 'add') {
        addPriceFrame = frameId
      } else {
        removePriceFrame = frameId
      }
      return
    }
    if (frameRef === 'add') {
      addPriceFrame = null
      return
    }
    removePriceFrame = null
  }
  const frameId = requestAnimationFrame(step)
  if (frameRef === 'add') {
    addPriceFrame = frameId
  } else {
    removePriceFrame = frameId
  }
}

watch(() => props.visible, (value) => {
  if (value && props.product && props.product.id) {
    showModal()
  } else if (isMounted.value) {
    hideModal()
  }
}, { immediate: true })

onBeforeUnmount(() => {
  clearLeaveTimer()
  unlockPageScroll()
  if (addPriceFrame) {
    cancelAnimationFrame(addPriceFrame)
    addPriceFrame = null
  }
  if (removePriceFrame) {
    cancelAnimationFrame(removePriceFrame)
    removePriceFrame = null
  }
})

const activeTab = ref<'total' | 'per100'>('total')

// Храним количество для модификаторов с max_count > 1
// selectedModifiers и modifierQuantities уже объявлены выше
const productSelectedModifiers = computed(() => {
  const modifiers = menuModel.selectedModifiersMap[props.product.id]
  return modifiers ? [...modifiers] : []
})

const modifierGroups = computed(() => props.product.modifier_groups || [])
// Храним выбранные модификаторы для каждой группы: для max_count > 1 - массив, для max_count <= 1 - один ID или null
// groupSelections уже объявлен выше
const requiredGroupError = ref<string | null>(null)
const modifiersError = ref<string | null>(null)

// Получаем суммарный min_count для списка модификаторов "Дополнительно" (без групп)
const modifiersMinCount = computed(() => {
  if (!props.product.modifiers || props.product.modifiers.length === 0) {
    return 0
  }

  const groupModifierIds = new Set<number>()
  modifierGroups.value.forEach((group) => {
    group.modifiers?.forEach((modifier) => {
      groupModifierIds.add(modifier.id)
    })
  })

  const minCounts = props.product.modifiers
    .filter(modifier => !groupModifierIds.has(modifier.id))
    .map(modifier => modifier.min_count || 0)
    .filter(minCount => minCount > 0)

  if (!minCounts.length) {
    return 0
  }

  return minCounts.reduce((sum, value) => sum + value, 0)
})

const syncGroupSelections = () => {
  if (!modifierGroups.value.length) {
    groupSelections.value = {}
    return
  }

  const next: Record<number, number[] | number | null> = {}
  modifierGroups.value.forEach((group) => {
    const groupMaxCount = group.max_count || 1
    // Собираем все вхождения модификаторов группы из selectedModifiers
    // (модификатор может быть выбран несколько раз)
    const selectedInGroup: number[] = []
    if (group.modifiers) {
      selectedModifiers.value.forEach((modifierId) => {
        if (group.modifiers?.some(m => m.id === modifierId)) {
          selectedInGroup.push(modifierId)
        }
      })
    }
    if (groupMaxCount > 1) {
      // Для групп с max_count > 1 храним массив выбранных модификаторов
      // (включая повторения, если модификатор выбран несколько раз)
      next[group.id] = selectedInGroup
    } else {
      // Для групп с max_count <= 1 храним один выбранный модификатор или null
      next[group.id] = selectedInGroup.length > 0 ? selectedInGroup[0] : null
    }
  })

  groupSelections.value = next
}

// const donenessOptions = [
//   { value: 'rare', label: 'Rare (с кровью)', price: 0 },
//   { value: 'medium', label: 'Medium (средняя)', price: 0 },
//   { value: 'well-done', label: 'Well Done (прожаренный)', price: 0 }
// ]
// const selectedDoneness = ref<string>(donenessOptions[1].value)

// const volumeOptions = [
//   { value: 'small', label: 'Маленький 200 мл', price: 0 },
//   { value: 'big', label: 'Большой 300 мл', price: 100 }
// ]
// const selectedVolume = ref<string>(volumeOptions[0].value)

// const sizeOptions = [
//   { value: '25', label: '25 см', price: 0 },
//   { value: '30', label: '30 см', price: 200 },
//   { value: '35', label: '35 см', price: 400 }
// ]
// const selectedSize = ref<string>(sizeOptions[0].value)

// const doughOptions = [
//   { value: 'thin', label: 'Тонкое', price: 0 },
//   { value: 'thick', label: 'Пышное', price: 100 }
// ]
// const selectedDough = ref<string>(doughOptions[0].value)

const isNutritionValueSet = (value: number | null | undefined) => {
  return value !== null && value !== undefined
}

const hasNutritionBlock = (block?: NutritionInfo | null) => {
  return Boolean(
    block &&
    isNutritionValueSet(block.protein) &&
    isNutritionValueSet(block.carbs) &&
    isNutritionValueSet(block.fat)
  )
}

const hasTotalNutrition = computed(() => {
  return Boolean(props.product.nutrition_info?.show_total && hasNutritionBlock(props.product.nutrition_info?.total))
})

const hasPer100gNutrition = computed(() => {
  return Boolean(props.product.nutrition_info?.show_per_100g && hasNutritionBlock(props.product.nutrition_info?.per_100g))
})

// показывать обе вкладки или только одну
const showBothTabs = computed(() => {
  return hasTotalNutrition.value && hasPer100gNutrition.value
})

// доступная вкладка
if (hasPer100gNutrition.value && !hasTotalNutrition.value) {
  activeTab.value = 'per100'
}

if (hasTotalNutrition.value) {
  activeTab.value = 'total'
}

const switchTab = (tab: 'total' | 'per100') => {
  if (
    (tab === 'total' && props.product.nutrition_info?.show_total) ||
    (tab === 'per100' && props.product.nutrition_info?.show_per_100g)
  ) {
    activeTab.value = tab
  }
}

const hasNutritionInfo = computed(() => {
  return hasTotalNutrition.value || hasPer100gNutrition.value
})

const hasCaloriesValue = (block?: NutritionInfo | null) => {
  return Boolean(block && isNutritionValueSet(block.calories))
}

const caloriesOnlyValue = computed(() => {
  if (hasNutritionInfo.value) {
    return null
  }

  const nutrition = props.product.nutrition_info
  if (!nutrition) {
    return null
  }

  if (nutrition.show_total && hasCaloriesValue(nutrition.total)) {
    return nutrition.total.calories
  }

  if (nutrition.show_per_100g && hasCaloriesValue(nutrition.per_100g)) {
    return nutrition.per_100g.calories
  }

  return null
})

const displayValues = computed(() => {
  if (!props.product.nutrition_info) {
    return {
      calories: 0,
      protein: 0,
      fat: 0,
      carbs: 0
    }
  }

  return activeTab.value === 'total'
    ? props.product.nutrition_info.total
    : props.product.nutrition_info.per_100g
})

const name = computed(() => {
  return props.product.internal_name &&
    props.product.internal_name !== '-' &&
    props.product.internal_name !== 'null'
    ? props.product.internal_name
    : props.product.name
})

const useAutoImageHeight = computed(() => autoImageHeightRestaurantCodes.has(restaurantModel.code))

const labels = computed(() => {
  const arr = []
  for (const key in props.product.labels) {
    const label = key as keyof ITransformLabels // Показываем TS что label это ключ из ITransformLabels
    const labelData = props.product.labels[label]
    if (typeof labelData !== 'boolean' && labelData.status) {
      arr.push(props.product.labels[label] as ILabel)
    }
  }
  return arr
})

const weight = computed(() => {
  let result = ''
  if (props.product.count) {
    result = `${props.product.count}`

    if (props.product.unit) { result += `&nbsp;${props.product.unit}` }
  }

  if (props.product.calories) {
    result += ` ${props.product.calories}&nbsp;ккал`
  }
  return result.trim()
})

const description = computed(() => {
  const desc = props.product.internal_description
  return desc ? desc.replace(/\n/g, '<br/>') : null
})

const ingredients = computed(() => {
  const ingredients = props.product.ingredients
  return ingredients ? ingredients.replace(/\n/g, '<br/>') : null
})

const allModifiersMap = computed(() => {
  const map = new Map<number, IModifier>()
  props.product.modifiers?.forEach((modifier) => {
    map.set(modifier.id, modifier)
  })
  props.product.modifier_groups?.forEach((group) => {
    group.modifiers?.forEach((modifier) => {
      map.set(modifier.id, modifier)
    })
  })
  return map
})

// Проверяет, принадлежит ли модификатор какой-либо группе
const isModifierInGroup = (modifierId: number): boolean => {
  return modifierGroups.value.some(group =>
    group.modifiers?.some(modifier => modifier.id === modifierId)
  )
}

// Функция для получения количества модификатора (учитывает min_count)
const getModifierQuantity = (modifierId: number): number => {
  const quantity = modifierQuantities.value[modifierId]
  // Если количество не установлено, но у модификатора min_count > 0, возвращаем min_count
  if (quantity === undefined || quantity === 0) {
    const modifier = allModifiersMap.value.get(modifierId)
    if (modifier && modifier.min_count > 0 && modifier.max_count > 1) {
      return modifier.min_count
    }
  }
  return quantity || 0
}

// Количество выбранного модификатора из блока "Дополнительно" с учетом min_count
const getAdditionalModifierSelectedCount = (modifier: IModifier): number => {
  if (isModifierInGroup(modifier.id)) {
    return 0
  }
  if (modifier.max_count > 1) {
    return getModifierQuantity(modifier.id)
  }
  return selectedModifiers.value.includes(modifier.id) ? 1 : 0
}

// Суммарное количество выбранных модификаторов из блока "Дополнительно"
const getAdditionalModifiersTotalCount = (): number => {
  if (!props.product.modifiers) {
    return 0
  }
  return props.product.modifiers.reduce((sum, modifier) => {
    return sum + getAdditionalModifierSelectedCount(modifier)
  }, 0)
}

// Проверка, что все обязательные модификаторы из блока "Дополнительно" выбраны
const hasValidAdditionalModifiersSelection = (): boolean => {
  if (props.product.modifiers && props.product.modifiers.length) {
    for (const modifier of props.product.modifiers) {
      const minCount = modifier.min_count || 0
      if (minCount > 0 && getAdditionalModifierSelectedCount(modifier) < minCount) {
        return false
      }
    }
  }

  if (modifiersMinCount.value > 0 && getAdditionalModifiersTotalCount() < modifiersMinCount.value) {
    return false
  }

  return true
}

const totalPrice = computed(() => {
  // Учитываем недоступные модификаторы: из стоп-листа и отмеченные в корзине
  const unavailableFromCart = props.fromCart && props.cartItem
    ? menuModel.unavailableModifierIds.get(props.cartItem.cartItemId || String(props.cartItem.id))
    : null
  const isUnavailableForPrice = (modifier: any): boolean => {
    if (!modifier) {
      return false
    }
    if (unavailableFromCart?.has(modifier.id)) {
      return true
    }
    return isModifierInStopList(modifier.id, modifier.external_id, modifier.external_product_id)
  }

  let price = props.product.price

  // Добавляем цену модификаторов с количеством из блока "Дополнительно" (используем getModifierQuantity для учета min_count)
  if (props.product.modifiers) {
    props.product.modifiers.forEach((modifier) => {
      // Только модификаторы из блока "Дополнительно" с max_count > 1 учитываются через quantity
      if (modifier.max_count > 1 && !isModifierInGroup(modifier.id)) {
        const quantity = getModifierQuantity(modifier.id)
        if (quantity > 0 && !isUnavailableForPrice(modifier)) {
          price += modifier.price * quantity
        }
      }
    })
  }
  // Добавляем цену групповых модификаторов с учетом их количества
  modifierGroups.value.forEach((group) => {
    const selection = groupSelections.value[group.id]
    if (selection !== null && selection !== undefined) {
      if (Array.isArray(selection)) {
        // Если можно выбрать несколько модификаторов в группе
        selection.forEach((modifierId) => {
          const modifier = allModifiersMap.value.get(modifierId)
          if (modifier && !isUnavailableForPrice(modifier)) {
            price += modifier.price
          }
        })
      } else {
        // Если можно выбрать только один модификатор в группе
        const modifier = allModifiersMap.value.get(selection as number)
        if (modifier && !isUnavailableForPrice(modifier)) {
          price += modifier.price
        }
      }
    }
  })
  // Добавляем цену простых модификаторов из блока "Дополнительно" (max_count <= 1)
  if (selectedModifiers.value.length > 0) {
    selectedModifiers.value.forEach((modifierId) => {
      const modifier = allModifiersMap.value.get(modifierId)
      if (modifier && !isModifierInGroup(modifierId) && modifier.max_count <= 1 && !isUnavailableForPrice(modifier)) {
        price += modifier.price
      }
    })
  }
  return price
})

// Собирает все выбранные модификаторы (включая те, что с количеством) в один массив
const getAllSelectedModifiers = (): number[] => {
  const modifiersWithQuantity: number[] = []
  // Добавляем групповые модификаторы из groupSelections
  modifierGroups.value.forEach((group) => {
    const selection = groupSelections.value[group.id]
    if (selection !== null && selection !== undefined) {
      if (Array.isArray(selection)) {
        // Если можно выбрать несколько модификаторов в группе
        selection.forEach((modifierId) => {
          modifiersWithQuantity.push(modifierId)
        })
      } else {
        // Если можно выбрать только один модификатор в группе
        modifiersWithQuantity.push(selection as number)
      }
    }
  })
  // Добавляем модификаторы с количеством из блока "Дополнительно" (используем getModifierQuantity для учета min_count)
  if (props.product.modifiers) {
    props.product.modifiers.forEach((modifier) => {
      // Пропускаем модификаторы, которые уже в группах
      if (isModifierInGroup(modifier.id)) {
        return
      }
      if (modifier.max_count > 1) {
        const quantity = getModifierQuantity(modifier.id)
        if (quantity > 0) {
          for (let i = 0; i < quantity; i++) {
            modifiersWithQuantity.push(modifier.id)
          }
        }
      }
    })
  }
  // Добавляем модификаторы без количества из блока "Дополнительно" (старый формат)
  selectedModifiers.value.forEach((modifierId) => {
    // Пропускаем модификаторы, которые уже в группах
    if (isModifierInGroup(modifierId)) {
      return
    }
    const modifier = allModifiersMap.value.get(modifierId)
    if (modifier && modifier.max_count <= 1) {
      modifiersWithQuantity.push(modifierId)
    }
  })
  return modifiersWithQuantity
}

// Вычисляет количество конкретного варианта товара с текущими модификаторами в корзине
const productInCart = computed(() => {
  // Если открыто как новое (для замены), возвращаем 0, чтобы показывалась кнопка "Заказать"
  if (props.cartItem?._openAsNew) {
    return 0
  }
  // Если открыто из корзины, возвращаем количество из cartItem
  if (props.fromCart && props.cartItem) {
    return props.cartItem.inCart || 0
  }
  // Иначе ищем в корзине по текущим модификаторам
  const allModifiers = getAllSelectedModifiers()
  const size = selectedSizeId.value || null
  const cartItemId = menuModel.generateCartItemId(props.product.id, allModifiers, size)
  const cartItem = menuModel.cart.find((item: any) => item.cartItemId === cartItemId)
  return cartItem?.inCart || 0
})

// Получаем все элементы корзины для этого продукта
// Каждая позиция отображается отдельно, даже если они одинаковые
// Исключаем элементы с модификаторами в стоп-листе
const productCartItems = computed(() => {
  const items: any[] = []
  menuModel.cart
    .filter((item: any) => {
      // Фильтруем по id продукта и количеству
      if (item.id !== props.product.id || !item.inCart || item.inCart <= 0) {
        return false
      }
      // Исключаем элементы с модификаторами в стоп-листе
      const cartItemId = item.cartItemId || String(item.id)
      const unavailableModifiers = menuModel.unavailableModifierIds.get(cartItemId)
      if (unavailableModifiers && unavailableModifiers.size > 0) {
        return false
      }
      return true
    })
    .forEach((item: any) => {
      // Добавляем каждый элемент столько раз, сколько его количество в корзине
      for (let i = 0; i < (item.inCart || 0); i++) {
        items.push({
          ...item,
          // Добавляем уникальный ключ для каждого экземпляра
          uniqueKey: `${item.cartItemId}_${i}`
        })
      }
    })
  return items
})

const isCartItemsVisible = computed(() => {
  const isDisabled = isCartDisabledForTable(restaurantModel.code, tableId.value)
  return restaurantModel.isCartEnabled && productCartItems.value.length > 0 && !isDisabled
})

// Общее количество товаров в корзине для этого продукта
// Теперь это просто длина массива productCartItems, так как каждый элемент уже размножен
const totalCartItemsCount = computed(() => {
  return productCartItems.value.length
})

// Состояние раскрытия списка товаров в корзине
const isCartItemsExpanded = ref(false)
const isCartItemsExpandedForChevron = ref(false) // Для синхронизации chevron
const cartItemsListRef = ref<HTMLElement | null>(null)
const cartItemsListHeight = ref<string>('0px')
const MAX_CART_ITEMS_HEIGHT = 200

// Переключение состояния раскрытия
const toggleCartItemsExpanded = () => {
  if (!cartItemsListRef.value) {
    return
  }

  if (!isCartItemsExpanded.value) {
    // Измеряем реальную высоту перед раскрытием
    cartItemsListRef.value.style.maxHeight = 'none'
    const scrollHeight = cartItemsListRef.value.scrollHeight
    // Если элементов больше 3, ограничиваем высоту, иначе используем реальную высоту
    const height = productCartItems.value.length > 3
      ? Math.min(scrollHeight, MAX_CART_ITEMS_HEIGHT)
      : scrollHeight
    cartItemsListRef.value.style.maxHeight = '0px'
    // Сразу поворачиваем chevron
    isCartItemsExpandedForChevron.value = true
    // Используем requestAnimationFrame для плавной анимации
    requestAnimationFrame(() => {
      cartItemsListHeight.value = `${height}px`
      isCartItemsExpanded.value = true
    })
  } else {
    // Сразу поворачиваем chevron обратно
    isCartItemsExpandedForChevron.value = false
    // Сразу убираем класс expanded для плавного закрытия
    isCartItemsExpanded.value = false
    // Начинаем анимацию закрытия
    requestAnimationFrame(() => {
      cartItemsListHeight.value = '0px'
    })
  }
}

// Получение описания элемента корзины (модификаторы и размер)
const getCartItemDescription = (item: any): string => {
  const parts: string[] = []
  const modifiers = item.selectedModifiers || []
  const size = item.selectedSize || null

  // Получаем названия модификаторов
  if (modifiers.length > 0) {
    // Собираем все доступные модификаторы (из блока "Дополнительно" и из групп)
    const available: any[] = []
    if (Array.isArray(item.modifiers)) {
      available.push(...item.modifiers)
    }
    if (Array.isArray(item.modifier_groups)) {
      item.modifier_groups.forEach((group: any) => {
        if (Array.isArray(group.modifiers)) {
          available.push(...group.modifiers)
        }
      })
    }

    // Если нет доступных модификаторов в item, используем allModifiersMap из props.product
    if (available.length === 0) {
      modifiers.forEach((modifierId: number) => {
        const modifier = allModifiersMap.value.get(modifierId)
        if (modifier) {
          available.push(modifier)
        }
      })
    }

    if (available.length > 0) {
      // Группируем модификаторы по ID и считаем количество
      const modifierCountMap = new Map<number, { modifier: any, count: number }>()
      modifiers.forEach((modifierId: number) => {
        const modifier = available.find((m: any) => m.id === modifierId)
        if (modifier) {
          const existing = modifierCountMap.get(modifierId)
          if (existing) {
            existing.count += 1
          } else {
            modifierCountMap.set(modifierId, { modifier, count: 1 })
          }
        }
      })

      // Формируем строку с модификаторами
      const modifierNames: string[] = []
      modifierCountMap.forEach(({ modifier, count }) => {
        if (count > 1) {
          modifierNames.push(`${modifier.name} - ${count}шт`)
        } else {
          modifierNames.push(modifier.name)
        }
      })

      if (modifierNames.length > 0) {
        parts.push(modifierNames.join(', '))
      }
    }
  }

  // Добавляем размер, если есть
  if (size && props.product.size) {
    const sizeOption = props.product.size.find((s: any) => s.id === size)
    if (sizeOption) {
      parts.push(sizeOption.name)
    }
  }

  return parts.length > 0 ? parts.join(', ') : 'Без модификаторов'
}

// Удаление конкретного элемента корзины (только один элемент)
const removeCartItem = (item: any) => {
  menuModel.addInCart({
    id: props.product.id,
    operator: 'minus',
    restName: restaurantModel.code,
    cartItemId: item.cartItemId
  })
}
const quantityDirection = ref<'up' | 'down'>('up')
const currentQuantity = computed(() => (productInCart.value > 0 ? productInCart.value : selectedQuantity.value))
const quantityTransitionName = computed(() => (quantityDirection.value === 'up' ? 'quantity-up' : 'quantity-down'))
const canDecrease = computed(() => (productInCart.value > 0 ? productInCart.value > 1 : selectedQuantity.value > 1))
const formatPrice = (value: number) => `${Math.round(value / 100).toLocaleString('ru-RU')} ₽`
const addPriceValue = computed(() => totalPrice.value)
const removePriceValue = computed(() => totalPrice.value)
const displayedAddPrice = ref<number>(addPriceValue.value)
const displayedRemovePrice = ref<number>(removePriceValue.value)
const addButtonPrice = computed(() => formatPrice(displayedAddPrice.value))
const removeButtonPrice = computed(() => formatPrice(displayedRemovePrice.value))

const currentPriceValue = computed(() => productInCart.value > 0 ? displayedRemovePrice.value : displayedAddPrice.value)
const priceDigitsCount = computed(() => {
  const priceInRubles = Math.round(currentPriceValue.value / 100)
  return priceInRubles.toString().length
})
const priceMinWidth = computed(() => {
  if (priceDigitsCount.value >= 4) {
    return '53px'
  }
  if (priceDigitsCount.value === 3) {
    return '41px'
  }
  if (priceDigitsCount.value === 2) {
    return '32px'
  }
  return '22px' // для 1
})

const isModifierSelected = (modifierId: number): boolean => {
  return selectedModifiers.value.includes(modifierId) || getModifierQuantity(modifierId) > 0
}

const isGroupModifierSelected = (groupId: number, modifierId: number): boolean => {
  const selection = groupSelections.value[groupId]
  if (!selection) {
    return false
  }
  if (Array.isArray(selection)) {
    return selection.includes(modifierId)
  }
  return selection === modifierId
}

// Получает количество выбранных экземпляров модификатора в группе
const getGroupModifierCount = (groupId: number, modifierId: number): number => {
  const selection = groupSelections.value[groupId]
  if (!selection) {
    return 0
  }
  if (Array.isArray(selection)) {
    return selection.filter(id => id === modifierId).length
  }
  return selection === modifierId ? 1 : 0
}

// Увеличивает количество группового модификатора
const increaseGroupModifierQuantity = (groupId: number, modifierId: number, maxCount: number) => {
  const modifier = allModifiersMap.value.get(modifierId)
  // Если модификатор в стоп-листе, не позволяем его выбрать
  if (modifier && isModifierInStopList(modifierId, modifier.external_id, modifier.external_product_id)) {
    return
  }
  const group = modifierGroups.value.find(item => item.id === groupId)
  if (!group) {
    return
  }

  const groupMaxCount = group.max_count || 1
  const currentSelection = groupSelections.value[groupId]

  // Получаем текущий список выбранных модификаторов в группе
  let selectedInGroup: number[] = []
  if (Array.isArray(currentSelection)) {
    selectedInGroup = [...currentSelection]
  } else if (currentSelection !== null && currentSelection !== undefined) {
    selectedInGroup = [currentSelection]
  }

  // Подсчитываем количество выбранных экземпляров этого модификатора
  const modifierCount = selectedInGroup.filter(id => id === modifierId).length

  // Проверяем ограничения модификатора (max_count модификатора)
  if (modifierCount >= maxCount) {
    return
  }

  // Проверяем ограничения группы: общее количество выбранных модификаторов в группе
  // Если groupMaxCount > 1, проверяем общее количество выбранных элементов в группе
  // Если groupMaxCount = 1, это группа с одиночным выбором, но мы все равно можем добавить несколько экземпляров одного модификатора
  if (groupMaxCount > 1) {
    // Проверяем, не превышает ли добавление еще одного экземпляра лимит группы
    // Если группа заполнена (selectedInGroup.length >= groupMaxCount), нельзя добавить еще один элемент
    // Это правильное поведение: группа позволяет только groupMaxCount элементов
    if (selectedInGroup.length >= groupMaxCount) {
      return
    }
  }

  // Добавляем еще один экземпляр модификатора
  selectedInGroup.push(modifierId)
  sendModifierSelectedMetric()

  // Удаляем все модификаторы этой группы из selectedModifiers
  group.modifiers?.forEach((mod) => {
    while (selectedModifiers.value.includes(mod.id)) {
      const index = selectedModifiers.value.indexOf(mod.id)
      selectedModifiers.value.splice(index, 1)
    }
  })

  // Добавляем выбранные модификаторы обратно
  selectedInGroup.forEach((id) => {
    selectedModifiers.value.push(id)
  })

  menuModel.setSelectedModifiers(props.product.id, [...selectedModifiers.value])
  syncGroupSelections()

  requiredGroupError.value = null

  if (props.product.inCart && props.product.inCart > 0) {
    menuModel.updateCartForProduct(props.product.id, restaurantModel.code)
  }
}

// Инициализация модификаторов с min_count > 0
const initializeModifiersWithMinCount = () => {
  let hasChanges = false

  // Инициализируем модификаторы из блока "Дополнительно"
  if (props.product.modifiers) {
    props.product.modifiers.forEach((modifier) => {
      // Пропускаем модификаторы, которые в группах
      if (isModifierInGroup(modifier.id)) {
        return
      }
      if (modifier.min_count > 0 && modifier.max_count > 1) {
        // Если модификатор еще не установлен, устанавливаем min_count
        if (modifierQuantities.value[modifier.id] === undefined) {
          modifierQuantities.value = {
            ...modifierQuantities.value,
            [modifier.id]: modifier.min_count
          }
          hasChanges = true
        }
      }
      if (modifier.min_count > 0 && modifier.max_count <= 1) {
        if (!selectedModifiers.value.includes(modifier.id)) {
          selectedModifiers.value.push(modifier.id)
          hasChanges = true
        }
      }
    })
  }

  // Инициализируем групповые модификаторы с min_count > 0
  modifierGroups.value.forEach((group) => {
    group.modifiers?.forEach((modifier) => {
      if (modifier.min_count > 0 && modifier.max_count > 1) {
        const currentSelection = groupSelections.value[group.id]
        let selectedInGroup: number[] = []
        if (Array.isArray(currentSelection)) {
          selectedInGroup = [...currentSelection]
        } else if (currentSelection !== null && currentSelection !== undefined) {
          selectedInGroup = [currentSelection]
        }

        // Подсчитываем количество выбранных экземпляров этого модификатора
        const modifierCount = selectedInGroup.filter(id => id === modifier.id).length

        // Если количество меньше min_count, добавляем недостающие
        if (modifierCount < modifier.min_count) {
          const needed = modifier.min_count - modifierCount
          for (let i = 0; i < needed; i++) {
            selectedInGroup.push(modifier.id)
          }

          // Обновляем groupSelections
          const groupMaxCount = group.max_count || 1
          if (groupMaxCount > 1) {
            groupSelections.value = {
              ...groupSelections.value,
              [group.id]: selectedInGroup
            }
          } else {
            groupSelections.value = {
              ...groupSelections.value,
              [group.id]: selectedInGroup[0] || null
            }
          }

          // Обновляем selectedModifiers
          group.modifiers?.forEach((mod) => {
            while (selectedModifiers.value.includes(mod.id)) {
              const index = selectedModifiers.value.indexOf(mod.id)
              selectedModifiers.value.splice(index, 1)
            }
          })

          selectedInGroup.forEach((id) => {
            selectedModifiers.value.push(id)
          })

          hasChanges = true
        }
      }
    })
  })
  if (hasChanges) {
    updateSelectedModifiersFromQuantities()
  }
}

const increaseModifierQuantity = (modifierId: number, maxCount: number) => {
  const modifier = allModifiersMap.value.get(modifierId)
  // Если модификатор в стоп-листе, не позволяем его выбрать
  if (modifier && isModifierInStopList(modifierId, modifier.external_id, modifier.external_product_id)) {
    return
  }
  const currentQuantity = getModifierQuantity(modifierId)
  if (currentQuantity < maxCount) {
    modifierQuantities.value = {
      ...modifierQuantities.value,
      [modifierId]: currentQuantity + 1
    }
    updateSelectedModifiersFromQuantities()
    modifiersError.value = null
    sendModifierSelectedMetric()
    // Если продукт уже в корзине, обновляем цену в корзине
    if (props.product.inCart && props.product.inCart > 0) {
      menuModel.updateCartForProduct(props.product.id, restaurantModel.code)
    }
  }
}
const decreaseModifierQuantity = (modifierId: number) => {
  const modifier = allModifiersMap.value.get(modifierId)
  if (!modifier) { return }
  const currentQuantity = modifierQuantities.value[modifierId] || 0
  const minCount = modifier.min_count || 0
  const newQuantity = currentQuantity - 1
  // Не позволяем уменьшать ниже min_count
  if (newQuantity < minCount) {
    return
  }
  if (newQuantity === 0) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [modifierId]: _, ...rest } = modifierQuantities.value
    modifierQuantities.value = rest
  } else {
    modifierQuantities.value = {
      ...modifierQuantities.value,
      [modifierId]: newQuantity
    }
  }
  updateSelectedModifiersFromQuantities()
  modifiersError.value = null
  // Если продукт уже в корзине, обновляем цену в корзине
  if (props.product.inCart && props.product.inCart > 0) {
    menuModel.updateCartForProduct(props.product.id, restaurantModel.code)
  }
}

const updateSelectedModifiersFromQuantities = () => {
  const modifiersWithQuantity: number[] = []
  // Добавляем модификаторы с количеством (используем getModifierQuantity для учета min_count)
  if (props.product.modifiers) {
    props.product.modifiers.forEach((modifier) => {
      if (modifier.max_count > 1) {
        const quantity = getModifierQuantity(modifier.id)
        if (quantity > 0) {
          for (let i = 0; i < quantity; i++) {
            modifiersWithQuantity.push(modifier.id)
          }
        }
      }
    })
  }
  // Добавляем модификаторы без количества (старый формат)
  selectedModifiers.value.forEach((modifierId) => {
    const modifier = allModifiersMap.value.get(modifierId)
    if (modifier && modifier.max_count <= 1) {
      modifiersWithQuantity.push(modifierId)
    }
  })
  menuModel.setSelectedModifiers(props.product.id, modifiersWithQuantity)
}

const getPositionWordForMinimum = (count: number): string => {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) {
    return 'позицию'
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return 'позиции'
  }
  return 'позиций'
}

const toggleModifier = (modifierId: number) => {
  const modifier = allModifiersMap.value.get(modifierId)
  // Если у модификатора max_count > 1, используем контролы количества
  if (modifier && modifier.max_count > 1) {
    return
  }

  const index = selectedModifiers.value.indexOf(modifierId)
  if (index > -1) {
    // Убираем модификатор
    selectedModifiers.value.splice(index, 1)
    modifiersError.value = null
  } else {
    // Добавляем модификатор - без ограничений на количество
    selectedModifiers.value.push(modifierId)
    modifiersError.value = null
    sendModifierSelectedMetric()
  }
  updateSelectedModifiersFromQuantities()
  // Если продукт уже в корзине, обновляем цену в корзине
  if (props.product.inCart && props.product.inCart > 0) {
    menuModel.updateCartForProduct(props.product.id, restaurantModel.code)
  }
}

const isSizeSelected = (sizeId: string): boolean => {
  return selectedSizeId.value === sizeId
}

const toggleSize = (sizeId: string) => {
  if (selectedSizeId.value === sizeId) {
    selectedSizeId.value = null
    menuModel.setSelectedSize(props.product.id, null)
  } else {
    selectedSizeId.value = sizeId
    menuModel.setSelectedSize(props.product.id, sizeId)
  }
  // Если продукт уже в корзине, обновляем корзину
  if (props.product.inCart && props.product.inCart > 0) {
    menuModel.updateCartForProduct(props.product.id, restaurantModel.code)
  }
}

// const selectDoneness = (value: string) => {
//   selectedDoneness.value = value
// }
//
// const selectVolume = (value: string) => {
//   selectedVolume.value = value
// }
//
// const selectSize = (value: string) => {
//   selectedSize.value = value
// }
//
// const selectDough = (value: string) => {
//   selectedDough.value = value
// }

// Обработчик клика на групповой модификатор
const handleGroupModifierClick = (groupId: number, modifierId: number, modifierMaxCount: number) => {
  // Если у модификатора max_count > 1, используем функцию увеличения количества
  if (modifierMaxCount > 1) {
    const currentCount = getGroupModifierCount(groupId, modifierId)
    if (currentCount > 0) {
      // Если модификатор уже выбран, уменьшаем количество
      selectGroupModifier(groupId, modifierId)
    } else {
      // Если модификатор не выбран, увеличиваем количество (добавляем)
      increaseGroupModifierQuantity(groupId, modifierId, modifierMaxCount)
    }
  } else {
    // Если max_count <= 1, используем обычную функцию выбора
    selectGroupModifier(groupId, modifierId)
  }
}

const selectGroupModifier = (groupId: number, modifierId: number) => {
  const group = modifierGroups.value.find(item => item.id === groupId)
  if (!group) {
    return
  }

  const groupMaxCount = group.max_count || 1
  const currentSelection = groupSelections.value[groupId]

  // Получаем модификатор для проверки его max_count и min_count
  const modifier = group.modifiers?.find(m => m.id === modifierId)
  if (!modifier) {
    return
  }

  const modifierMinCount = modifier.min_count || 0

  // Получаем текущий список выбранных модификаторов в группе
  let selectedInGroup: number[] = []
  if (Array.isArray(currentSelection)) {
    selectedInGroup = [...currentSelection]
  } else if (currentSelection !== null && currentSelection !== undefined) {
    selectedInGroup = [currentSelection]
  } else {
    // Если selection еще не инициализирован, проверяем selectedModifiers напрямую
    selectedInGroup = group.modifiers?.filter(mod => selectedModifiers.value.includes(mod.id)).map(m => m.id) || []
  }

  // Подсчитываем количество выбранных экземпляров этого модификатора
  const modifierCount = selectedInGroup.filter(id => id === modifierId).length

  // Определяем, что делать с модификатором
  if (modifierCount > 0) {
    // Модификатор уже выбран - убираем один экземпляр, но проверяем min_count
    if (modifierMinCount > 0 && modifierCount <= modifierMinCount) {
      // Нельзя удалить, если это приведет к нарушению min_count модификатора
      return
    }
    // Удаляем один экземпляр модификатора
    const firstIndex = selectedInGroup.indexOf(modifierId)
    selectedInGroup.splice(firstIndex, 1)
  } else {
    // Модификатор не выбран - добавляем его
    sendModifierSelectedMetric()
    if (groupMaxCount > 1) {
      // Для групп с множественным выбором проверяем общий max_count группы
      if (selectedInGroup.length >= groupMaxCount) {
        return
      }
    } else {
      // Для групп с одиночным выбором заменяем выбранный модификатор
      selectedInGroup = []
    }
    // Проверяем max_count модификатора - если он больше 1, можно добавить несколько раз
    // Но при первом добавлении добавляем только один раз
    selectedInGroup.push(modifierId)
  }

  // Удаляем все модификаторы этой группы из selectedModifiers
  group.modifiers?.forEach((mod) => {
    // Удаляем все экземпляры этого модификатора
    while (selectedModifiers.value.includes(mod.id)) {
      const index = selectedModifiers.value.indexOf(mod.id)
      selectedModifiers.value.splice(index, 1)
    }
  })

  // Добавляем выбранные модификаторы обратно
  selectedInGroup.forEach((id) => {
    selectedModifiers.value.push(id)
  })

  menuModel.setSelectedModifiers(props.product.id, [...selectedModifiers.value])
  syncGroupSelections()

  requiredGroupError.value = null

  if (props.product.inCart && props.product.inCart > 0) {
    menuModel.updateCartForProduct(props.product.id, restaurantModel.code)
  }
}

watch(productSelectedModifiers, (modifiers) => {
  if (Array.isArray(modifiers)) {
    // Разделяем модификаторы на те, что с количеством и без
    const quantities: Record<number, number> = {}
    const simpleModifiers: number[] = []
    modifiers.forEach((modifierId) => {
      const modifier = allModifiersMap.value.get(modifierId)
      if (modifier && modifier.max_count > 1) {
        quantities[modifierId] = (quantities[modifierId] || 0) + 1
      } else {
        simpleModifiers.push(modifierId)
      }
    })
    modifierQuantities.value = quantities
    selectedModifiers.value = simpleModifiers
  } else {
    selectedModifiers.value = []
    modifierQuantities.value = {}
  }
  syncGroupSelections()
}, { immediate: true })

// Перезагружаем модификаторы и размер при смене продукта
watch(() => props.product.id, () => {
  // Если открыто из корзины, не перезагружаем модификаторы (они уже загружены в showModal)
  if (props.fromCart && props.cartItem) {
    return
  }

  const modifiers = menuModel.getSelectedModifiers(props.product.id)

  // Восстанавливаем количества модификаторов
  const quantities: Record<number, number> = {}
  const simpleModifiers: number[] = []

  if (modifiers && modifiers.length > 0) {
    modifiers.forEach((modifierId) => {
      const modifier = allModifiersMap.value.get(modifierId)
      if (modifier && modifier.max_count > 1) {
        quantities[modifierId] = (quantities[modifierId] || 0) + 1
      } else {
        simpleModifiers.push(modifierId)
      }
    })
  }

  modifierQuantities.value = quantities
  selectedModifiers.value = simpleModifiers
  syncGroupSelections()
  selectedQuantity.value = 1
  selectedSizeId.value = menuModel.getSelectedSize(props.product.id)
  // Инициализируем модификаторы с min_count > 0 после загрузки
  nextTick(() => {
    initializeModifiersWithMinCount()
  })
})

// Отслеживаем изменения в selectedModifiersMap для текущего продукта
watch(() => menuModel.selectedModifiersMap[props.product.id], (modifiers) => {
  if (modifiers) {
    // Разделяем модификаторы на те, что с количеством и без
    // Групповые модификаторы всегда идут в simpleModifiers, так как их количество контролируется группой
    const quantities: Record<number, number> = {}
    const simpleModifiers: number[] = []
    modifiers.forEach((modifierId) => {
      // Проверяем, является ли модификатор групповым
      const isInGroup = isModifierInGroup(modifierId)
      const modifier = allModifiersMap.value.get(modifierId)
      // Групповые модификаторы всегда идут в simpleModifiers
      // Только модификаторы из блока "Дополнительно" с max_count > 1 попадают в quantities
      if (!isInGroup && modifier && modifier.max_count > 1) {
        quantities[modifierId] = (quantities[modifierId] || 0) + 1
      } else {
        simpleModifiers.push(modifierId)
      }
    })
    modifierQuantities.value = quantities
    selectedModifiers.value = simpleModifiers
  } else {
    selectedModifiers.value = []
    modifierQuantities.value = {}
  }
  syncGroupSelections()
  // Инициализируем модификаторы с min_count > 0 после обновления
  nextTick(() => {
    initializeModifiersWithMinCount()
  })
}, { immediate: true, deep: true })

// Отслеживаем изменения в selectedSizesMap для текущего продукта
watch(() => menuModel.selectedSizesMap[props.product.id], (sizeId) => {
  if (sizeId !== undefined && sizeId !== selectedSizeId.value) {
    selectedSizeId.value = sizeId || null
  }
}, { immediate: true, deep: true })

watch(() => props.product.modifier_groups, () => {
  syncGroupSelections()
}, { immediate: true, deep: true })

watch(() => props.product.modifiers, () => {
  // При обновлении списка модификаторов переинициализируем обязательные
  nextTick(() => {
    initializeModifiersWithMinCount()
  })
}, { immediate: true, deep: true })

watch(() => props.product.product_picture, () => {
  loadProductImage()
}, { deep: true })

watch(() => props.product.id, () => {
  isImagePreviewOpen.value = false
  // При смене блюда переинициализируем обязательные модификаторы
  nextTick(() => {
    initializeModifiersWithMinCount()
  })
})

watch(productInCart, (value, oldValue) => {
  if (value === 0) {
    selectedQuantity.value = 1
    // Если продукт удален из корзины, очищаем размер
    selectedSizeId.value = null
    menuModel.clearSelectedSize(props.product.id)
  }
  if (value !== oldValue && typeof oldValue === 'number') {
    quantityDirection.value = value > (oldValue || 0) ? 'up' : 'down'
  }
})

// Отслеживаем изменения количества выбранных модификаторов для очистки ошибок
watch(
  () => ({
    modifiers: [...selectedModifiers.value],
    quantities: { ...modifierQuantities.value }
  }),
  () => {
    if (modifiersError.value && hasValidAdditionalModifiersSelection()) {
      modifiersError.value = null
    }
  },
  { deep: true }
)

watch(currentQuantity, (value, oldValue) => {
  if (value !== oldValue && typeof oldValue === 'number') {
    quantityDirection.value = value > (oldValue || 0) ? 'up' : 'down'
  }
})

watch(addPriceValue, (value) => {
  animatePriceValue(displayedAddPrice, value, 'add')
}, { immediate: true })

watch(removePriceValue, (value) => {
  animatePriceValue(displayedRemovePrice, value, 'remove')
}, { immediate: true })

onMounted(() => {
  loadProductImage()
  // Загружаем сохраненный размер при открытии модалки
  const savedSize = menuModel.getSelectedSize(props.product.id)
  if (savedSize) {
    selectedSizeId.value = savedSize
  }
  // Инициализируем модификаторы с min_count > 0
  nextTick(() => {
    initializeModifiersWithMinCount()
  })
})

const ensureRequiredGroupSelected = (): boolean => {
  // Проверяем минимальное количество выбранных модификаторов в каждой группе
  for (const group of modifierGroups.value) {
    const groupMinCount = group.min_count || 0
    if (groupMinCount > 0) {
      const selection = groupSelections.value[group.id]
      let selectedCount = 0
      if (Array.isArray(selection)) {
        selectedCount = selection.length
      } else if (selection !== null && selection !== undefined) {
        selectedCount = 1
      }
      if (selectedCount < groupMinCount) {
        const maxCount = group.max_count || 1
        if (maxCount === 1) {
          requiredGroupError.value = `Выберите вариант в группе "${group.name || 'Обязательно'}"`
        } else {
          requiredGroupError.value = `Выберите минимум ${groupMinCount} ${groupMinCount === 1 ? 'вариант' : groupMinCount >= 2 && groupMinCount <= 4 ? 'варианта' : 'вариантов'} в группе "${group.name || 'Обязательно'}"`
        }
        return false
      }
    }
  }
  requiredGroupError.value = null

  // Проверяем обязательные модификаторы из блока "Дополнительно"
  if (props.product.modifiers && props.product.modifiers.length > 0) {
    for (const modifier of props.product.modifiers) {
      const minCount = modifier.min_count || 0
      if (minCount > 0 && getAdditionalModifierSelectedCount(modifier) < minCount) {
        modifiersError.value = `Добавьте минимум ${minCount} ${getPositionWordForMinimum(minCount)} "${modifier.name}"`
        return false
      }
    }
  }

  // Проверяем минимальное количество модификаторов "Дополнительно" (с учетом количества)
  const totalModifiersCount = getAdditionalModifiersTotalCount()
  if (modifiersMinCount.value > 0 && totalModifiersCount < modifiersMinCount.value) {
    modifiersError.value = `Выберите минимум ${modifiersMinCount.value} ${getPositionWordForMinimum(modifiersMinCount.value)}`
    return false
  }

  modifiersError.value = null
  return true
}

const addSelectedToCart = () => {
  if (!ensureRequiredGroupSelected()) {
    return
  }
  // Сохраняем выбранный размер перед добавлением в корзину
  if (selectedSizeId.value) {
    menuModel.setSelectedSize(props.product.id, selectedSizeId.value)
  }
  // Собираем все модификаторы для передачи в корзину
  const allModifiers = getAllSelectedModifiers()
  // Сохраняем модификаторы в модель для совместимости
  menuModel.setSelectedModifiers(props.product.id, allModifiers)
  amplitudeLogEvent(amplitudeEvents.CHOICE_ITEM_FOR_CART)

  // Отправляем событие в Яндекс.Метрику только при успешном добавлении
  if (typeof window !== 'undefined' && (window as any).ym) {
    (window as any).ym(process.env.YA_METRIK_ID, 'reachGoal', 'addToCart')
  }

  if (props.fromCart && props.cartItem) {
    // Проверяем, нужно ли удалить старое блюдо (если открыто как новое для замены)
    const shouldReplace = props.cartItem?._openAsNew || false

    if (shouldReplace && props.cartItem) {
      // Удаляем старое блюдо из корзины перед добавлением нового
      const oldCartItemId = props.cartItem.cartItemId
      const newCartItemId = menuModel.generateCartItemId(props.product.id, allModifiers, selectedSizeId.value || null)
      const oldItemCount = props.cartItem.inCart || 0
      for (let i = 0; i < oldItemCount; i++) {
        menuModel.addInCart({
          id: props.product.id,
          operator: 'minus',
          restName: restaurantModel.code,
          cartItemId: oldCartItemId
        })
      }
      // Чистим флаги недоступных модификаторов для старого и нового cartItem
      menuModel.unavailableModifierIds.delete(oldCartItemId)
      menuModel.unavailableModifierIds.delete(newCartItemId)
      // Добавляем новое блюдо
      for (let i = 0; i < selectedQuantity.value; i++) {
        menuModel.addInCart({
          id: props.product.id,
          operator: 'plus',
          restName: restaurantModel.code,
          selectedModifiers: allModifiers,
          selectedSize: selectedSizeId.value || null
        })
      }
    } else {
      // При открытии из корзины - обновляем конкретный элемент корзины
      const newCartItemId = menuModel.generateCartItemId(props.product.id, allModifiers, selectedSizeId.value || null)
      const oldCartItemId = props.cartItem.cartItemId

      // Если модификаторы изменились, создаем новый элемент и удаляем старый
      if (newCartItemId !== oldCartItemId) {
        // Удаляем старый элемент
        const oldItemCount = props.cartItem.inCart || 0
        for (let i = 0; i < oldItemCount; i++) {
          menuModel.addInCart({
            id: props.product.id,
            operator: 'minus',
            restName: restaurantModel.code,
            cartItemId: oldCartItemId
          })
        }
        // Чистим флаги недоступных модификаторов для старого и нового cartItem
        menuModel.unavailableModifierIds.delete(oldCartItemId)
        menuModel.unavailableModifierIds.delete(newCartItemId)
        // Добавляем новый элемент с новыми модификаторами
        for (let i = 0; i < oldItemCount; i++) {
          menuModel.addInCart({
            id: props.product.id,
            operator: 'plus',
            restName: restaurantModel.code,
            selectedModifiers: allModifiers,
            selectedSize: selectedSizeId.value || null
          })
        }
      } else {
        // Модификаторы не изменились, просто обновляем количество если нужно
        const currentCount = productInCart.value
        const targetCount = selectedQuantity.value
        if (targetCount > currentCount) {
          for (let i = 0; i < (targetCount - currentCount); i++) {
            menuModel.addInCart({
              id: props.product.id,
              operator: 'plus',
              restName: restaurantModel.code,
              cartItemId: oldCartItemId
            })
          }
        } else if (targetCount < currentCount) {
          for (let i = 0; i < (currentCount - targetCount); i++) {
            menuModel.addInCart({
              id: props.product.id,
              operator: 'minus',
              restName: restaurantModel.code,
              cartItemId: oldCartItemId
            })
          }
        }
      }
    }
  } else {
    // При открытии из меню - добавляем в корзину как обычно
    for (let i = 0; i < selectedQuantity.value; i++) {
      menuModel.addInCart({
        id: props.product.id,
        operator: 'plus',
        restName: restaurantModel.code,
        selectedModifiers: allModifiers,
        selectedSize: selectedSizeId.value || null
      })
    }
  }

  selectedQuantity.value = 1
  emit('close')
}

const removeProductFromCart = () => {
  if (props.fromCart && props.cartItem) {
    // При открытии из корзины - удаляем конкретный элемент корзины
    const cartItemId = props.cartItem.cartItemId
    const count = props.cartItem.inCart || 0

    for (let i = 0; i < count; i++) {
      menuModel.addInCart({
        id: props.product.id,
        operator: 'minus',
        restName: restaurantModel.code,
        cartItemId
      })
    }
  } else {
    // При открытии из меню - удаляем по текущим модификаторам
    const allModifiers = getAllSelectedModifiers()
    const size = selectedSizeId.value || null
    const cartItemId = menuModel.generateCartItemId(props.product.id, allModifiers, size)
    const cartItem = menuModel.cart.find((item: any) => item.cartItemId === cartItemId)
    const count = cartItem?.inCart || 0

    for (let i = 0; i < count; i++) {
      menuModel.addInCart({
        id: props.product.id,
        operator: 'minus',
        restName: restaurantModel.code,
        cartItemId
      })
    }
  }

  // Очищаем модификаторы и размер только если это был последний вариант в корзине
  // (проверяем, остались ли еще варианты этого товара в корзине)
  const remainingVariants = menuModel.cart.filter((item: any) => item.id === props.product.id)
  if (remainingVariants.length === 0) {
    menuModel.clearSelectedModifiers(props.product.id)
    menuModel.clearSelectedSize(props.product.id)
    selectedModifiers.value = []
    modifierQuantities.value = {}
    selectedSizeId.value = null
    syncGroupSelections()
  }
  selectedQuantity.value = 1
  emit('close')
}

const handlePrimaryAction = () => {
  if (productInCart.value > 0) {
    removeProductFromCart()
  } else {
    addSelectedToCart()
  }
}

const handlePrimaryActionWithMetric = () => {
  handlePrimaryAction()
}

const sendModifierSelectedMetric = () => {
  if (typeof window !== 'undefined' && (window as any).ym) {
    (window as any).ym(process.env.YA_METRIK_ID, 'reachGoal', 'modifierSelected')
  }
}

const increaseQuantity = () => {
  if (productInCart.value > 0) {
    // Если товар уже в корзине, увеличиваем количество
    menuModel.addInCart({ id: props.product.id, operator: 'plus', restName: restaurantModel.code })
  } else {
    // Если товара нет в корзине, увеличиваем selectedQuantity
    selectedQuantity.value += 1
  }
}

const decreaseQuantity = () => {
  if (productInCart.value > 0) {
    // Если товар в корзине, уменьшаем количество или удаляем
    if (productInCart.value > 1) {
      menuModel.addInCart({ id: props.product.id, operator: 'minus', restName: restaurantModel.code })
    } else {
      // Если количество = 1, удаляем товар из корзины
      removeProductFromCart()
    }
  } else if (selectedQuantity.value > 1) {
    // Если товара нет в корзине, уменьшаем selectedQuantity (но не меньше 1)
    selectedQuantity.value -= 1
  }
}

return { __sfc: true,props, emit, themeModel, restaurantModel, menuModel, route, autoImageHeightRestaurantCodes, stopListModifierIds, stopListExternalModifierIds, stopListGlobalExternalModifierIds, stopListExternalProductIds, tableId, isAddControlsVisible, imgSrc, isImageLoading, isMounted, isVisible, isImagePreviewOpen, lastLoadedImageKey, ANIMATION_DURATION, leaveTimer, addPriceFrame, removePriceFrame, previewImageSrc, selectedSizeId, selectedModifiers, modifierQuantities, selectedQuantity, groupSelections, contentWrapperRef, touchStartY, handleContentTouchStart, handleContentTouchMove, lockPageScroll, unlockPageScroll, clearLeaveTimer, getImageKey, loadProductImage, handleImageLoaded, handleImageError, openImagePreview, closeImagePreview, isModifierInStopList, showModal, hideModal, animatePriceValue, activeTab, productSelectedModifiers, modifierGroups, requiredGroupError, modifiersError, modifiersMinCount, syncGroupSelections, isNutritionValueSet, hasNutritionBlock, hasTotalNutrition, hasPer100gNutrition, showBothTabs, switchTab, hasNutritionInfo, hasCaloriesValue, caloriesOnlyValue, displayValues, name, useAutoImageHeight, labels, weight, description, ingredients, allModifiersMap, isModifierInGroup, getModifierQuantity, getAdditionalModifierSelectedCount, getAdditionalModifiersTotalCount, hasValidAdditionalModifiersSelection, totalPrice, getAllSelectedModifiers, productInCart, productCartItems, isCartItemsVisible, totalCartItemsCount, isCartItemsExpanded, isCartItemsExpandedForChevron, cartItemsListRef, cartItemsListHeight, MAX_CART_ITEMS_HEIGHT, toggleCartItemsExpanded, getCartItemDescription, removeCartItem, quantityDirection, currentQuantity, quantityTransitionName, canDecrease, formatPrice, addPriceValue, removePriceValue, displayedAddPrice, displayedRemovePrice, addButtonPrice, removeButtonPrice, currentPriceValue, priceDigitsCount, priceMinWidth, isModifierSelected, isGroupModifierSelected, getGroupModifierCount, increaseGroupModifierQuantity, initializeModifiersWithMinCount, increaseModifierQuantity, decreaseModifierQuantity, updateSelectedModifiersFromQuantities, getPositionWordForMinimum, toggleModifier, isSizeSelected, toggleSize, handleGroupModifierClick, selectGroupModifier, ensureRequiredGroupSelected, addSelectedToCart, removeProductFromCart, handlePrimaryAction, handlePrimaryActionWithMetric, sendModifierSelectedMetric, increaseQuantity, decreaseQuantity, IconLotPlaceholder, IconClose }
}

})